// src/services/experimentalApi.ts

import { createApi } from '@reduxjs/toolkit/query/react'
import baseQueryWithReauth from './baseQuery' // Assuming you have a base query with re-authentication logic
import * as GeoTIFF from 'geotiff'

// Declare the window property for TypeScript
declare global {
  interface Window {
    lastWmsImageUrl?: string
  }
}

export interface ExperimentalVersionResponse {
  version: string
}

export interface WmsCapabilitiesRequest {
  connect_id: string
  username: string
  password: string
}

export interface WmsCapabilitiesResponse {
  capabilities: any // Replace with proper type when known
}

export interface WmsMapRequest {
  connect_id: string
  username: string
  password: string
  bbox: string
  width: number
  height: number
}

export interface WmsMapResponse {
  imageUrl: string
  error?: string
  gsd?: number // Ground Sample Distance in meters/pixel
  metadata?: {
    width: number
    height: number
    samplesPerPixel: number
    bitsPerSample: number[]
    photometric: number
  }
}

// Add helper function for GSD calculation
const calculateGSD = (
  image: GeoTIFF.GeoTIFFImage,
  bbox: number[],
  geoKeys: any
): { gsd: number; details: any } => {
  const [minX, minY, maxX, maxY] = bbox
  const geographicWidth = maxX - minX
  const geographicHeight = maxY - minY
  const pixelWidth = image.getWidth()
  const pixelHeight = image.getHeight()

  // Check if using WGS84 (EPSG:4326)
  const isWGS84 = geoKeys && geoKeys.GeographicTypeGeoKey === 4326
  let gsd: number
  let details: any = {}

  if (isWGS84) {
    // For WGS84, convert degrees to meters
    const centerLat = (minY + maxY) / 2
    const METERS_PER_DEGREE_LAT = 111320
    const METERS_PER_DEGREE_LON = Math.cos((centerLat * Math.PI) / 180) * 111320

    // Convert geographic dimensions to meters
    const widthMeters = Math.abs(geographicWidth) * METERS_PER_DEGREE_LON
    const heightMeters = Math.abs(geographicHeight) * METERS_PER_DEGREE_LAT

    // Calculate GSD in meters/pixel
    const gsdXMeters = widthMeters / pixelWidth
    const gsdYMeters = heightMeters / pixelHeight
    gsd = Math.max(gsdXMeters, gsdYMeters)

    details = {
      method: 'WGS84',
      centerLat,
      widthMeters,
      heightMeters,
      gsdXMeters,
      gsdYMeters,
    }
  } else {
    // If not WGS84, use the raw resolution values
    const [gsdX, gsdY] = image.getResolution()
    gsd = Math.max(Math.abs(gsdX), Math.abs(gsdY))
    details = {
      method: 'raw',
      gsdX,
      gsdY,
    }
  }

  return { gsd, details }
}

export const experimentalApi = createApi({
  reducerPath: 'experimentalApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['WmsCapabilities', 'WmsMap'],
  endpoints: (builder) => ({
    getExperimentalVersion: builder.query<ExperimentalVersionResponse, void>({
      query: () => ({
        url: '/experimental',
        method: 'GET',
      }),
    }),

    getWmsCapabilities: builder.mutation<WmsCapabilitiesResponse, WmsCapabilitiesRequest>({
      query: (credentials) => ({
        url: '/experimental/wms/capabilities',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['WmsCapabilities'],
    }),

    getWmsMap: builder.mutation<WmsMapResponse, WmsMapRequest>({
      query: (request) => ({
        url: '/experimental/wms/map',
        method: 'POST',
        body: request,
        responseHandler: async (response) => {
          // Check content type to determine how to handle the response
          const contentType = response.headers.get('content-type')

          if (contentType?.includes('xml')) {
            // Handle XML response (likely an error)
            const text = await response.text()
            const errorMatch = text.match(/<ServiceException>(.*?)<\/ServiceException>/s)
            const errorMessage = errorMatch ? errorMatch[1].trim() : 'Unknown WMS error'
            return Promise.reject({ error: errorMessage })
          }

          try {
            // Get the response as ArrayBuffer
            const arrayBuffer = await response.arrayBuffer()

            // Create a Blob from the ArrayBuffer with TIFF mime type
            const tiffBlob = new Blob([arrayBuffer], { type: 'image/tiff' })

            // Create canvas
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')

            if (!ctx) {
              throw new Error('Could not get canvas context')
            }

            // Process the TIFF using the same method as drag-and-drop
            const tiff = await GeoTIFF.fromBlob(tiffBlob)
            const image = await tiff.getImage()

            // Set canvas dimensions
            canvas.width = image.getWidth()
            canvas.height = image.getHeight()

            // Get image metadata and GSD
            const fileDirectory = image.getFileDirectory()
            const geoKeys = image.getGeoKeys()
            const bbox = image.getBoundingBox()

            // Calculate GSD
            let gsdInfo = { gsd: 0, details: {} }
            if (bbox) {
              gsdInfo = calculateGSD(image, bbox, geoKeys)
              console.log('GSD Calculation:', {
                ...gsdInfo.details,
                finalGsd: {
                  meters: gsdInfo.gsd.toFixed(6) + ' m/px',
                  centimeters: (gsdInfo.gsd * 100).toFixed(4) + ' cm/px',
                },
              })
            }

            // Get coordinate reference system info if available
            if (geoKeys) {
              console.log('WMS GeoTIFF GeoKeys:', geoKeys)
            }

            // Calculate and log physical dimensions
            const bboxInfo = bbox
              ? {
                  boundingBox: {
                    minX: bbox[0],
                    minY: bbox[1],
                    maxX: bbox[2],
                    maxY: bbox[3],
                    width: {
                      meters: bbox[2] - bbox[0],
                      centimeters: (bbox[2] - bbox[0]) * 100,
                    },
                    height: {
                      meters: bbox[3] - bbox[1],
                      centimeters: (bbox[3] - bbox[1]) * 100,
                    },
                  },
                  pixelDimensions: {
                    width: image.getWidth(),
                    height: image.getHeight(),
                  },
                  gsd: {
                    meters: gsdInfo.gsd,
                    centimeters: gsdInfo.gsd * 100,
                  },
                }
              : null

            // Log image type information
            const isRGB = fileDirectory.PhotometricInterpretation === 2
            const isYCbCr = fileDirectory.PhotometricInterpretation === 6
            const samples = image.getSamplesPerPixel()

            console.log('WMS GeoTIFF Image Type:', {
              photometricInterpretation: fileDirectory.PhotometricInterpretation,
              isRGB,
              isYCbCr,
              samplesPerPixel: samples,
              colorSpace: isRGB ? 'RGB' : isYCbCr ? 'YCbCr' : 'Other',
            })

            const metadata = {
              width: image.getWidth(),
              height: image.getHeight(),
              samplesPerPixel: image.getSamplesPerPixel(),
              bitsPerSample: image.getBitsPerSample(),
              photometric: fileDirectory.PhotometricInterpretation,
            }
            console.log('TIFF metadata:', metadata)
            console.log('GSD:', {
              meters: gsdInfo.gsd + ' m/px',
              centimeters: gsdInfo.gsd * 100 + ' cm/px',
            })

            // Get the data from all bands
            const data = (await image.readRasters()) as Float32Array[]

            // Create ImageData
            const imageData = ctx.createImageData(canvas.width, canvas.height)

            // Check if this is a standard RGB image
            if (isRGB && samples >= 3) {
              // For RGB images, use the values directly
              for (let i = 0; i < data[0].length; i++) {
                for (let band = 0; band < 3; band++) {
                  imageData.data[i * 4 + band] = Math.min(255, Math.max(0, data[band][i]))
                }
                imageData.data[i * 4 + 3] = 255 // Alpha channel
              }
            } else if (isYCbCr && samples >= 3) {
              // Convert YCbCr to RGB
              for (let i = 0; i < data[0].length; i++) {
                const Y = data[0][i]
                const Cb = data[1][i]
                const Cr = data[2][i]

                // YCbCr to RGB conversion
                const R = Y + 1.402 * (Cr - 128)
                const G = Y - 0.344136 * (Cb - 128) - 0.714136 * (Cr - 128)
                const B = Y + 1.772 * (Cb - 128)

                // Clamp values to 0-255 range
                imageData.data[i * 4] = Math.min(255, Math.max(0, R)) // R
                imageData.data[i * 4 + 1] = Math.min(255, Math.max(0, G)) // G
                imageData.data[i * 4 + 2] = Math.min(255, Math.max(0, B)) // B
                imageData.data[i * 4 + 3] = 255 // Alpha channel
              }
            } else {
              // For other image types, use grayscale
              for (let i = 0; i < data[0].length; i++) {
                const value = Math.min(255, Math.max(0, data[0][i]))
                imageData.data[i * 4] = value // R
                imageData.data[i * 4 + 1] = value // G
                imageData.data[i * 4 + 2] = value // B
                imageData.data[i * 4 + 3] = 255 // Alpha channel
              }
            }

            // Put the image data on the canvas
            ctx.putImageData(imageData, 0, 0)

            // Convert canvas to data URL - use PNG for higher quality
            const imageUrl = canvas.toDataURL('image/png')

            // Store the URL for later use
            window.lastWmsImageUrl = imageUrl

            // Return the response with metadata
            return {
              imageUrl,
              gsd: gsdInfo.gsd,
              metadata,
            }
          } catch (error) {
            return Promise.reject({ error: 'Failed to process the TIFF image' })
          }
        },
      }),
      invalidatesTags: ['WmsMap'],
    }),
  }),
})

// Export hooks for usage in functional components
export const {
  useGetExperimentalVersionQuery,
  useGetWmsCapabilitiesMutation,
  useGetWmsMapMutation,
} = experimentalApi

// Export the API for use in store configuration
export default experimentalApi
